import axios from 'axios';

/**
 * Freelance Gigs — typed API client.
 *
 * Centralises every call against /api/freelance-gigs/* so pages don't have to
 * remember endpoint paths or response envelopes. All methods unwrap the
 * `{ success, data }` envelope and throw on `success === false`.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface FreelanceGigAttachment {
  filename: string;
  original_name: string;
  mime_type: string;
  size: number;
}

export type FreelanceGigStatus = 'draft' | 'published' | 'closed';
export type FreelanceGigResponseStatus = 'interested' | 'withdrawn';

export interface FreelanceGig {
  id: number;
  employer_id: number;
  title: string;
  description: string;
  work_fields: string[];
  city: string | null;
  nationwide: boolean;
  budget_text: string | null;
  deadline_text: string | null;
  attachments: FreelanceGigAttachment[];
  status: FreelanceGigStatus;
  published_at: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FreelanceGigInput {
  title: string;
  description: string;
  work_fields: string[];
  city: string | null;
  nationwide: boolean;
  budget_text?: string | null;
  deadline_text?: string | null;
}

export interface FreelanceGigResponseRow {
  id: number;
  gig_id: number;
  employee_id: number;
  status: FreelanceGigResponseStatus;
  intro_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface FreelanceGigResponseWithEmployee extends FreelanceGigResponseRow {
  employee_email: string | null;
  employee_first_name: string | null;
  employee_last_name: string | null;
  unread_count: number;
}

export interface FreelanceGigResponseWithGig extends FreelanceGigResponseRow {
  gig: FreelanceGig;
  unread_count: number;
}

export interface FreelanceGigWithUnread extends FreelanceGig {
  unread_count: number;
}

/**
 * Browse-feed row carries the viewer's own response-state alongside the gig
 * so the list can show "you've responded" / unread badges without N+1 calls.
 */
export interface FreelanceGigFeedRow extends FreelanceGig {
  my_response_id: number | null;
  my_response_status: FreelanceGigResponseStatus | null;
  unread_count: number;
}

export interface FreelanceGigMessage {
  id: number;
  gig_id: number;
  response_id: number;
  from_user_id: number;
  body: string;
  read_at: string | null;
  created_at: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: Record<string, string>;
  message?: string;
}

async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const res = await promise;
  if (!res.data?.success) {
    const err: any = new Error(res.data?.error || 'Request failed');
    err.fieldErrors = res.data?.errors;
    throw err;
  }
  return res.data.data as T;
}

export const freelanceGigsApi = {
  // ─── Employer CRUD ─────────────────────────────────────────────────────────
  myGigs: (status?: FreelanceGigStatus) =>
    unwrap<{ gigs: FreelanceGigWithUnread[] }>(
      axios.get(`${API_URL}/freelance-gigs/my`, {
        params: status ? { status } : undefined,
        headers: authHeaders(),
      }),
    ),

  getById: (id: number) =>
    unwrap<{ gig: FreelanceGig }>(
      axios.get(`${API_URL}/freelance-gigs/${id}`, { headers: authHeaders() }),
    ),

  create: (input: FreelanceGigInput) =>
    unwrap<{ gig: FreelanceGig }>(
      axios.post(`${API_URL}/freelance-gigs`, input, { headers: authHeaders() }),
    ),

  update: (id: number, patch: Partial<FreelanceGigInput>) =>
    unwrap<{ gig: FreelanceGig }>(
      axios.put(`${API_URL}/freelance-gigs/${id}`, patch, { headers: authHeaders() }),
    ),

  publish: (id: number) =>
    unwrap<{ gig: FreelanceGig }>(
      axios.patch(`${API_URL}/freelance-gigs/${id}/publish`, {}, { headers: authHeaders() }),
    ),

  close: (id: number) =>
    unwrap<{ gig: FreelanceGig }>(
      axios.patch(`${API_URL}/freelance-gigs/${id}/close`, {}, { headers: authHeaders() }),
    ),

  remove: (id: number) =>
    unwrap<undefined>(
      axios.delete(`${API_URL}/freelance-gigs/${id}`, { headers: authHeaders() }),
    ),

  uploadAttachments: async (id: number, files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    const res = await axios.post(
      `${API_URL}/freelance-gigs/${id}/attachments`,
      fd,
      { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } },
    );
    if (!res.data?.success) throw new Error(res.data?.error || 'Upload failed');
    return res.data.data as { attachments: FreelanceGigAttachment[] };
  },

  deleteAttachment: (id: number, filename: string) =>
    unwrap<{ attachments: FreelanceGigAttachment[] }>(
      axios.delete(
        `${API_URL}/freelance-gigs/${id}/attachments/${encodeURIComponent(filename)}`,
        { headers: authHeaders() },
      ),
    ),

  responses: (id: number) =>
    unwrap<{ gig: FreelanceGig; responses: FreelanceGigResponseWithEmployee[] }>(
      axios.get(`${API_URL}/freelance-gigs/${id}/responses`, { headers: authHeaders() }),
    ),

  // ─── Employee browse + respond ─────────────────────────────────────────────
  publishedFeed: (limit = 50, offset = 0, sectors?: string[]) =>
    unwrap<{ gigs: FreelanceGigFeedRow[] }>(
      axios.get(`${API_URL}/freelance-gigs/published`, {
        params: {
          limit,
          offset,
          ...(sectors && sectors.length > 0 ? { sectors: sectors.join(',') } : {}),
        },
        headers: authHeaders(),
      }),
    ),

  publicGig: (id: number) =>
    unwrap<{ gig: FreelanceGig; my_response: FreelanceGigResponseRow | null }>(
      axios.get(`${API_URL}/freelance-gigs/published/${id}`, { headers: authHeaders() }),
    ),

  respond: (gigId: number, introMessage?: string) =>
    unwrap<{ response: FreelanceGigResponseRow }>(
      axios.post(
        `${API_URL}/freelance-gigs/${gigId}/respond`,
        introMessage !== undefined ? { intro_message: introMessage } : {},
        { headers: authHeaders() },
      ),
    ),

  withdraw: (responseId: number) =>
    unwrap<{ response: FreelanceGigResponseRow }>(
      axios.patch(
        `${API_URL}/freelance-gigs/responses/${responseId}/withdraw`,
        {},
        { headers: authHeaders() },
      ),
    ),

  myResponses: () =>
    unwrap<{ responses: FreelanceGigResponseWithGig[] }>(
      axios.get(`${API_URL}/freelance-gigs/responses/mine`, { headers: authHeaders() }),
    ),

  // ─── Thread (both parties) ─────────────────────────────────────────────────
  messages: (responseId: number) =>
    unwrap<{ messages: FreelanceGigMessage[] }>(
      axios.get(
        `${API_URL}/freelance-gigs/responses/${responseId}/messages`,
        { headers: authHeaders() },
      ),
    ),

  postMessage: (responseId: number, body: string) =>
    unwrap<{ message: FreelanceGigMessage }>(
      axios.post(
        `${API_URL}/freelance-gigs/responses/${responseId}/messages`,
        { body },
        { headers: authHeaders() },
      ),
    ),

  // Cheap COUNT(*) probe used by the global nav to decorate the entry with a
  // single badge (e.g. Freelance (3)) when there are unread messages.
  unreadSummary: () =>
    unwrap<{ total: number }>(
      axios.get(`${API_URL}/freelance-gigs/unread-summary`, {
        headers: authHeaders(),
      }),
    ),
};
